// src/pages/Checkout/Checkout.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart.js';
import { useAuth } from '../../hooks/useAuth.js';
import { useToast } from '../../hooks/useToast.js';
import { ordersAPI } from '../../api/orders.js';
import { consentAPI, CONSENT_TYPES } from '../../api/consent.js';
import { getAnonymousId } from '../../utils/anonymousId.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';
import { Breadcrumbs } from '../../components/common/Breadcrumbs/Breadcrumbs.jsx';
import { LayoutUploader } from '../../components/checkout/LayoutUploader/LayoutUploader.jsx';
import { useDispatch, useSelector } from 'react-redux';
import { openAuth } from '../../store/uiSlice.js';
import { getStickerCart, resetStickerCart } from '../../store/stickerCartSlice.js';
import { stickerCartAPI } from '../../api/stickerCart.js';
import { StickerDesignStep } from '../../components/checkout/StickerDesignStep/StickerDesignStep.jsx';

export const Checkout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useAuth();
  const { items, total, getCart, clearCart, isLoaded: isCartLoaded } = useCart();
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: '',
    city: '',
    postal_code: '',
    // 🔑 По умолчанию — Почта России: в корзине стикеры чаще всего, а
    //    «курьер» для наклеек мы не предлагаем (см. разбор в разметке
    //    способа получения). Если в корзине есть товары, выбор не
    //    теряется — там курьер доступен рядом.
    delivery_method: 'post',
    payment_method: 'card',
    notes: '',
  });
  // 🔑 СОГЛАСИЕ НА ОБРАБОТКУ ПДн — ГАЛОЧКА ПО УМОЛЧАНИЮ ПУСТАЯ.
  //    Предустановленная отметка = «согласие по умолчанию», которого закон
  //    не допускает (ч. 1 ст. 9 152-ФЗ: согласие конкретное, предметное и
  //    сознательное). Без галочки заказ не уйдёт.
  const [consentPd, setConsentPd] = useState(false);
  const [consentError, setConsentError] = useState('');

  // ============================================================
  // МАКЕТЫ КЛИЕНТА — ПО КАЖДОЙ ПОЗИЦИИ ОТДЕЛЬНО (этапы 1.1–1.3)
  // ============================================================
  // 🔑 Ключ — `item.id` (строка корзины), а НЕ `product_id`.
  //    Один и тот же товар может лежать в корзине дважды с РАЗНЫМИ
  //    макетами (два баннера разных размеров). Ключ по товару склеил бы
  //    их и потерял один файл.
  //
  //    Форма значения: { snapshot, pending }
  //      • `snapshot` — снимок от `POST /uploads/layout` либо `null`;
  //      • `pending`  — галочка «макета нет, пришлю позже».
  //    Оба поля живут вместе, потому что противоречат друг другу:
  //    компонент гасит `pending` при загрузке файла и наоборот.
  const [layouts, setLayouts] = useState({});

  // ============================================================
  // 🔴 СТИКЕРЫ В ОФОРМЛЕНИИ (п.87, 2026-10-29).
  //    Раньше страница знала только товары: со стикером в корзине она
  //    отправляла на /cart («корзина пуста»), и заказ наклеек оформить
  //    было нельзя вовсе. Теперь обе корзины видит одна страница, а
  //    заказ уходит ДВУМЯ запросами — у товаров и наклеек разные правила
  //    (и разные таблицы позиций).
  // ============================================================
  const stickerCart = useSelector((st) => st.stickerCart);
  // «Что печатать» — решение владельца: спрашиваем здесь, на оформлении.
  // По умолчанию «из каталога», если такие позиции есть: человек их уже
  // выбрал. Иначе — «макет ждём», чтобы не соврать, что картинка есть.
  const [designMode, setDesignMode] = useState('catalog');
  // Заказ отправлен — редирект «корзина пуста» отключается (см. ниже).
  const [orderPlaced, setOrderPlaced] = useState(false);

  useEffect(() => {
    dispatch(getStickerCart());
  }, [dispatch]);

  /** Обновить состояние макета одной позиции, не трогая остальные. */
  const setLayoutFor = (itemId, patch) => {
    setLayouts((prev) => ({
      ...prev,
      [itemId]: { ...(prev[itemId] || { snapshot: null, pending: false }), ...patch },
    }));
  };

  // ============================================================
  // 🔴 ЗДЕСЬ БЫЛ БАГ: `navigate('/cart')` вызывался ПРЯМО В РЕНДЕРЕ.
  //    Замер: страница отрисовывалась ПУСТОЙ (белая полоса) — навигация
  //    во время рендера не успевает примениться, а `return null` уже
  //    отдаёт пустой кадр.
  //
  //    ✅ Часть 1: навигация переехала в эффект — он выполняется ПОСЛЕ
  //       рендера, поэтому переход идёт без мигания.
  //
  //    🔴 Часть 2, более важная: проверка «корзина пуста» срабатывала
  //       ДО загрузки корзины. Замер: прямой заход на /checkout с
  //       товаром → мгновенный редирект на /cart, форма оформления не
  //       открывалась НИКОГДА. Причина — та же, что у страницы корзины:
  //       Redux на холодной загрузке пуст, а getCart() никто не звал.
  //
  //       ✅ Лечение: сначала запрашиваем корзину, и только потом
  //          решаем, пуста ли она. Ждём ИМЕННО `isCartLoaded`, а НЕ
  //          `isLoading`: последний равен false и ДО запроса, и ПОСЛЕ,
  //          поэтому не различает эти состояния.
  // ============================================================
  useEffect(() => {
    getCart();
  }, [getCart]);

  // 🔴 УСЛОВИЕ «КОРЗИНА ПУСТА» БЫЛО НЕВЕРНЫМ ДЛЯ СТИКЕРОВ.
  //    Оно проверяло ТОЛЬКО товарную корзину, поэтому человека со
  //    стикером в корзине сразу выкидывало на /cart — оформить заказ
  //    наклеек было НЕЛЬЗЯ. Теперь ждём загрузки ОБЕИХ корзин и
  //    уходим, только если пусты обе.
  const stickerReady = stickerCart.isLoaded;

  // 🔴 РЕДИРЕКТ СРАБАТЫВАЛ ПОСЛЕ УСПЕШНОГО ЗАКАЗА (поймано сквозным тестом).
  //    После создания заказа корзины очищаются, `bothEmpty` становится
  //    ИСТИННОЙ, и эффект уводил человека на /cart — в пустую корзину.
  //    Заказ при этом создан (201), но клиент его НЕ ВИДЕЛ и не знал
  //    номера: худший исход — человек оформляет второй раз.
  //    ✅ Лечение: редирект действует, только пока заказ НЕ отправлен.
  const bothEmpty = isCartLoaded && stickerReady && items.length === 0 && stickerCart.items.length === 0;

  useEffect(() => {
    if (bothEmpty && !orderPlaced) {
      navigate('/cart', { replace: true });
    }
  }, [bothEmpty, orderPlaced, navigate]);

  // Пока обе корзины не загружены — не решаем ничего (иначе мелькнёт
  // пустая страница или ложный редирект).
  if (!isCartLoaded || !stickerReady) {
    return null;
  }
  if (bothEmpty) {
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Проверяем согласие ДО запроса: иначе заказ создался бы, а согласие
    // осталось несобранным — то есть данные уже обрабатываются без основания.
    if (!consentPd) {
      setConsentError('Нужно согласие на обработку персональных данных');
      return;
    }
    setConsentError('');

    // ============================================================
    // 🔑 ПРОВЕРКА ПЕРЕД ОТПРАВКОЙ: АДРЕС ДЛЯ ПОЧТЫ (2026-10-29).
    //    Доставка Почтой России — 150 ₽ (решение владельца, STICKERS.md §7),
    //    но без адреса отправить некуда. Проверяем ДО запроса, чтобы
    //    заказ не создался «наполовину»: стикеры ушли бы, а товары нет.
    // ============================================================
    const isPostal = formData.delivery_method === 'post' || formData.delivery_method === 'courier';
    if (isPostal && !(formData.city.trim() && formData.address.trim())) {
      error('Укажите город и адрес доставки');
      return;
    }

    setIsLoading(true);
    try {
      const orderData = {
        // ⚠️ `user_id` НЕ отправляем: сервер берёт автора из токена
        //    (`req.user.id`). Раньше поле слалось отсюда, и это позволяло
        //    подставить чужой id. Гость просто не шлёт email/телефон
        //    авторизованного — сервер сам решит, гость это или нет.
        guest_email: isAuthenticated ? null : formData.email,
        guest_phone: isAuthenticated ? null : formData.phone,
        items: items.map(item => ({
          product_id: item.product_id,
          variant_id: item.variant_id || null,
          quantity: item.quantity,
          unit_price: item.price_at_time,
          calculator_data: item.calculator_data || {},
          // 🔑 МАКЕТ ПОЗИЦИИ (этапы 1.1–1.3). Отправляем СНИМОК, полученный
          //    от `POST /api/v1/uploads/layout`, а не сам файл: файл уже
          //    на сервере и проверен (MIME + магические байты).
          //    ⚠️ Сервер НЕ доверяет полю `layout_status` из тела — он
          //    выводит статус сам (`orderController.js`): файл → 'uploaded',
          //    обещание → 'pending', ничего → 'none'.
          layout_file: layouts[item.id]?.snapshot || null,
          layout_pending: Boolean(layouts[item.id]?.pending),
        })),
        // ⚠️ Индекс НЕ теряем: раньше `postal_code` собирался в форме, но в
        //    адрес не попадал — заказ уходил без индекса.
        delivery_address: [
          formData.postal_code,
          formData.city,
          formData.address,
        ].filter(Boolean).join(', '),
        delivery_method: formData.delivery_method,
        payment_method: formData.payment_method,
        customer_notes: formData.notes,
      };

      // ============================================================
      // 🔴 ДВА ЗАКАЗА — ПО ТИПУ ПОЗИЦИЙ (2026-10-29).
      //    У товаров и наклеек разные таблицы позиций и разные правила
      //    (доставка наклеек 150 ₽, товаров — «рассчитывается»).
      //    Поэтому: товары — `POST /orders`, наклейки — `POST /sticker-cart/order`.
      //    ⚠️ Отправляем ТОЛЬКО то, что есть: пустой `items: []` сервер
      //    отклонит («Order must have at least one item»), и это сломало
      //    бы покупку одних наклеек.
      //    📌 Два заказа вместо одного — временно и честно: у клиента
      //    будет ДВА номера. Когда появится общий заказ «товары + наклейки»,
      //    это надо будет свести в один (пункт в реестре).
      // ============================================================
      let productResponse = null;
      if (items.length > 0) {
        productResponse = await ordersAPI.create(orderData);
      }

      let stickerResponse = null;
      if (stickerCart.items.length > 0) {
        stickerResponse = await stickerCartAPI.createOrder({
          guest_email: isAuthenticated ? null : formData.email,
          guest_phone: isAuthenticated ? null : formData.phone,
          delivery_method: formData.delivery_method,
          delivery_address: orderData.delivery_address,
          payment_method: formData.payment_method,
          // Признак «макет ждём» уже сохранён в самой позиции корзины
          // (`design_status`), здесь его дублировать не нужно: сервер
          // читает состояние строки, а не слово клиента.
          customer_notes: formData.notes,
        });
      }

      // Какой заказ показать клиенту: товарный, если он есть, иначе наклейки.
      const response = productResponse || stickerResponse;

      // 🔑 ФИКСИРУЕМ СОГЛАСИЕ НА СЕРВЕРЕ, А НЕ В localStorage.
      //    По ч. 1 ст. 9 152-ФЗ оператор ОБЯЗАН ДОКАЗАТЬ наличие согласия;
      //    галочка в браузере не доказывает ничего — она исчезает вместе с
      //    очисткой хранилища и серверу не видна.
      //    ⚠️ Ошибку записи НЕ глотаем молча, но и НЕ отменяем заказ: он уже
      //    создан и человек его ждёт. Сообщаем честно — это лучше, чем
      //    сделать вид, что согласие сохранено.
      try {
        await consentAPI.accept({
          consent_type: CONSENT_TYPES.PERSONAL_DATA,
          document_version: '1.0',
          anonymous_id: getAnonymousId(),
        });
      } catch (consentErr) {
        console.error('Не удалось зафиксировать согласие:', consentErr);
        error('Заказ создан, но согласие не сохранилось. Сообщите нам, пожалуйста.');
      }

      // ⚠️ Очищаем ТОЛЬКО те корзины, чьи заказы реально созданы:
      //    `clearCart` (товары) и срез наклеек. Сервер уже удалил строки
      //    наклеек в транзакции; локальное состояние перечитываем, чтобы
      //    шапка не показывала старое число.
      // 🔑 ФЛАГ СТАВИМ ДО ОЧИСТКИ: иначе эффект успеет увидеть
      //    «корзина пуста» и увести на /cart вместо страницы заказа.
      setOrderPlaced(true);
      if (items.length > 0) await clearCart();
      if (stickerCart.items.length > 0) dispatch(resetStickerCart());
      success('Заказ оформлен успешно!');
      // ⚠️ Передаём email/телефон в `state`, чтобы страница заказа могла
      //    подтвердить владение для ГОСТЯ (публичный трекер требует их —
      //    иначе по номеру заказа читались бы чужие заказы). При перезагрузке
      //    страницы state теряется: тогда гость введёт данные вручную.
      // 🔴 АДРЕС ЗАКАЗА БРАЛИ ИЗ НЕВЕРНОГО ПОЛЯ (поймано сквозным тестом).
      //    У товарного заказа id лежит в `response.data.data.id`, а у
      //    заказа наклеек — в `response.data.data.order.id` (ответ
      //    `POST /sticker-cart/order` возвращает объект заказа внутри
      //    `order`, плюс позиции и суммы). Из-за этого после покупки
      //    ОДНИХ НАКЛЕЕК адрес становился `/orders/undefined`, и человек
      //    попадал на 500 вместо своей страницы заказа.
      //    ⚠️ Тот же класс, что §7 №1: неверное имя поля не даёт ошибки
      //       — оно даёт строку «undefined» и далёкий 500.
      const createdId = productResponse
        ? productResponse.data?.data?.id
        : stickerResponse?.data?.data?.order?.id;

      if (!createdId) {
        // Заказ создан, но адрес неизвестен — честно сказать, а не
        // вести на несуществующую страницу.
        success('Заказ оформлен. Мы свяжемся с вами по email.');
        return;
      }

      navigate(`/orders/${createdId}`, {
        state: isAuthenticated
          ? undefined
          : { guestEmail: formData.email, guestPhone: formData.phone },
      });
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось оформить заказ');
    } finally {
      setIsLoading(false);
    }
  };

  const breadcrumbs = [
    { label: 'Корзина', path: '/cart' },
    { label: 'Оформление заказа', path: '/checkout' },
  ];

  return (
    <div style={{ backgroundColor: 'rgb(var(--c-bg))', minHeight: '100vh', paddingBottom: '40px' }}>
      <div className="container" style={{ paddingTop: '24px' }}>
        <Breadcrumbs items={breadcrumbs} />

        <h1 style={{
          fontSize: '32px',
          fontWeight: 700,
          color: 'rgb(var(--c-text))',
          marginTop: '24px',
          marginBottom: '32px',
        }}>
          Оформление заказа
        </h1>

        <form onSubmit={handleSubmit}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            gap: '32px',
          }}>
            {/* Форма */}
            <div>
              <div style={{
                backgroundColor: 'rgb(var(--c-surface))',
                border: '1px solid rgb(var(--c-border))',
                borderRadius: '16px',
                padding: '24px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Контактные данные
                </h3>

                {!isAuthenticated && (
                  <>
                    {/* Гость оформляет заказ БЕЗ регистрации — это норма.
                        Но предлагаем войти: у авторизованного заказ сразу
                        привяжется к аккаунту и попадёт в «Мои заказы». */}
                    {/* 🔴 ЗДЕСЬ БЫЛИ ХАРДКОД-ЦВЕТА (`#F5F8FC` / `#DCE7F5` /
                        `#33475B`) — блок оставался БЕЛЫМ в тёмной теме и
                        светил ярким пятном посреди тёмной страницы.
                        Найдено замером 2026-10-09 (сессия 9).
                        🔑 Лечение — токен `--c-accent-soft`: он объявлен
                        в ОБЕИХ темах (`#E8EEF5` светлая, `#1E2B3C` тёмная)
                        именно для подложки акцентного блока.
                        Тот же класс дефекта, что п.83 реестра. */}
                    <div style={{
                      backgroundColor: 'rgb(var(--c-accent-soft))',
                      border: '1px solid rgb(var(--c-border))',
                      borderRadius: '8px',
                      padding: '12px',
                      marginBottom: '16px',
                      fontSize: '14px',
                      color: 'rgb(var(--c-text))',
                    }}>
                      Уже есть аккаунт?{' '}
                      {/* 🔑 МОДАЛКА, А НЕ ПЕРЕХОД (2026-10-29). Раньше ссылка
                          уводила на /login и теряла заполненную форму
                          оформления. Теперь модалка открывается поверх, и
                          введённые поля остаются на месте. */}
                      <button
                        type="button"
                        onClick={() => dispatch(openAuth({ mode: 'login' }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          padding: 0,
                          color: 'rgb(var(--c-accent))',
                          fontWeight: 600,
                          fontSize: 'inherit',
                          fontFamily: 'inherit',
                          cursor: 'pointer',
                          textDecoration: 'underline',
                        }}
                      >
                        Войдите
                      </button>{' '}
                      — заказ сохранится в личном кабинете. Или оформите
                      как гость, без регистрации.
                    </div>
                    <Input
                      label="Email *"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                    <div style={{ height: '12px' }} />
                    <Input
                      label="Телефон *"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                    />
                    <div style={{ height: '12px' }} />
                  </>
                )}

                <Input
                  label="Имя *"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                />
                <div style={{ height: '12px' }} />

                <Input
                  label="Фамилия"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                />
                <div style={{ height: '20px' }} />

                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Адрес доставки
                </h3>

                <Input
                  label="Город *"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  required
                />
                <div style={{ height: '12px' }} />

                <Input
                  label="Адрес (улица, дом, квартира) *"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
                <div style={{ height: '12px' }} />

                <Input
                  label="Индекс"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
                <div style={{ height: '20px' }} />

                {/* ============================================================
                    ШАГ 2: «КАКИЕ СТИКЕРЫ НАПЕЧАТАТЬ?» (п.87, 2026-10-29)
                    🔑 Показываем ТОЛЬКО если в корзине наклейки — иначе
                       вопрос про наклейки в заказе вывесок выглядел бы
                       поломкой.
                    ============================================================ */}
                {stickerCart.items.length > 0 && (
                  <div style={{
                    border: '1px dashed rgb(var(--c-accent))',
                    borderRadius: '14px',
                    padding: '16px',
                    marginBottom: '24px',
                  }}>
                    <StickerDesignStep
                      items={stickerCart.items}
                      mode={designMode}
                      onModeChange={setDesignMode}
                    />
                  </div>
                )}

                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Способ доставки
                </h3>

                {/* ============================================================
                    СПОСОБ ПОЛУЧЕНИЯ ЗАВИСИТ ОТ ТОГО, ЧТО В КОРЗИНЕ.
                    🔑 Решение владельца 2026-10-29: «да делаем» на доставку
                       Почтой России 150 ₽ / самовывоз бесплатно
                       (STICKERS.md §7).
                    ⚠️ Для НАКЛЕЕК «курьер» и «ТК» — неправда: мы их не
                       предлагаем. Показать их и взять 0 ₽ за доставку
                       значило бы обещать то, чего нет. Поэтому список
                       собирается по содержимому корзины.
                    ============================================================ */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  {stickerCart.items.length > 0 && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value="post"
                          checked={formData.delivery_method === 'post'}
                          onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                        />
                        Почта России — 150 ₽
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value="pickup"
                          checked={formData.delivery_method === 'pickup'}
                          onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                        />
                        Самовывоз (Старый Оскол) — бесплатно
                      </label>
                    </>
                  )}

                  {items.length > 0 && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value="courier"
                          checked={formData.delivery_method === 'courier'}
                          onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                        />
                        Курьером
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value="pickup"
                          checked={formData.delivery_method === 'pickup'}
                          onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                        />
                        Самовывоз
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          value="delivery"
                          checked={formData.delivery_method === 'delivery'}
                          onChange={(e) => setFormData({ ...formData, delivery_method: e.target.value })}
                        />
                        Доставка ТК
                      </label>
                    </>
                  )}
                </div>

                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Способ оплаты
                </h3>

                <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}>
                    <input
                      type="radio"
                      value="card"
                      checked={formData.payment_method === 'card'}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    />
                    Картой онлайн
                  </label>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                  }}>
                    <input
                      type="radio"
                      value="cash"
                      checked={formData.payment_method === 'cash'}
                      onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    />
                    Наличными
                  </label>
                </div>

                <Input
                  label="Комментарий к заказу"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Дополнительная информация"
                />
              </div>
            </div>

            {/* Итог */}
            <div>
              <div style={{
                backgroundColor: 'rgb(var(--c-surface))',
                border: '1px solid rgb(var(--c-border))',
                borderRadius: '16px',
                padding: '24px',
                position: 'sticky',
                top: '100px',
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: 600,
                  color: 'rgb(var(--c-text))',
                  marginBottom: '16px',
                }}>
                  Ваш заказ
                </h3>

                <div style={{ maxHeight: '420px', overflow: 'auto', marginBottom: '16px' }}>
                  {/* НАКЛЕЙКИ — в том же списке, что товары: клиент должен
                      видеть ВСЁ, что платит, а не только товары. */}
                  {stickerCart.items.map((line) => (
                    <div
                      key={line.id}
                      style={{
                        padding: '8px 0',
                        borderBottom: '1px solid rgb(var(--c-border))',
                        fontSize: '14px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span style={{ color: 'rgb(var(--c-text-secondary))' }}>
                          {line.title} · {Math.round(Number(line.size_mm) / 10)} см × {line.quantity}
                          {line.design_status === 'pending' && (
                            <b style={{ color: 'rgb(var(--c-text))', display: 'block', fontSize: 11.5, marginTop: 2 }}>
                              макет ждём
                            </b>
                          )}
                        </span>
                        <span style={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {Number(line.line_total).toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                    </div>
                  ))}
                  {items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        padding: '8px 0',
                        borderBottom: '1px solid rgb(var(--c-border))',
                        fontSize: '14px',
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}>
                        <span style={{ color: 'rgb(var(--c-text-secondary))' }}>
                          {item.Product?.name} × {item.quantity}
                        </span>
                        <span style={{ fontWeight: 500 }}>
                          {item.price_at_time * item.quantity} ₽
                        </span>
                      </div>

                      {/*
                        🔑 ПРИЁМ МАКЕТА — ВНУТРИ КАЖДОЙ ПОЗИЦИИ, А НЕ ОДИН
                        НА ЗАКАЗ. Причина и разбор — в шапке
                        `LayoutUploader.jsx` и в `orderController.js`:
                        у разных позиций разные файлы (баннер — один,
                        вывеска — другой).
                      */}
                      <LayoutUploader
                        layout={layouts[item.id]?.snapshot || null}
                        pending={Boolean(layouts[item.id]?.pending)}
                        onChange={(snapshot) => setLayoutFor(item.id, { snapshot })}
                        onPendingChange={(pending) => setLayoutFor(item.id, { pending })}
                      />
                    </div>
                  ))}
                </div>

                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '12px 0',
                  borderTop: '2px solid rgb(var(--c-border))',
                  fontSize: '18px',
                  fontWeight: 700,
                }}>
                  {/* 🔑 ИТОГ СОБИРАЕТСЯ ЗДЕСЬ ТОЛЬКО ДЛЯ ПОКАЗА.
                      Заказ сервер всё равно пересчитает по своим правилам
                      и вернёт свою сумму — клиентская здесь лишь подсказка. */}
                  <span>Итого</span>
                  <span>
                    {(Number(total || 0) + Number(stickerCart.total || 0)
                      + (stickerCart.items.length > 0 && formData.delivery_method === 'post' ? 150 : 0)
                    ).toLocaleString('ru-RU')} ₽
                  </span>
                </div>

                {/* 🔑 СОГЛАСИЕ НА ОБРАБОТКУ ПЕРСОНАЛЬНЫХ ДАННЫХ.
                    Заказ собирает email, телефон и адрес — это персональные
                    данные, и по ч. 1 ст. 9 152-ФЗ оператор ОБЯЗАН доказать
                    наличие согласия. Без галочки заказ не оформить.

                    ⚠️ ГАЛОЧКА ПО УМОЛЧАНИЮ ПУСТАЯ: предустановленная отметка
                    = «согласие по умолчанию», которого закон не допускает.
                    ⚠️ Показываем ВСЕГДА, а не только гостю: у авторизованного
                    заказ тоже содержит новые данные (адрес доставки), и
                    согласие на конкретную обработку нужно отдельно. */}
                <div style={{ marginTop: '20px' }}>
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      fontSize: '13px',
                      color: 'rgb(var(--c-text-secondary))',
                      lineHeight: 1.5,
                      cursor: 'pointer',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={consentPd}
                      onChange={(e) => setConsentPd(e.target.checked)}
                      style={{ marginTop: '3px', flexShrink: 0 }}
                    />
                    <span>
                      Я согласен на обработку персональных данных в соответствии
                      с{' '}
                      <Link
                        to="/privacy"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'rgb(var(--c-accent))', fontWeight: 600 }}
                      >
                        политикой конфиденциальности
                      </Link>
                    </span>
                  </label>
                  {consentError && (
                    <div style={{ color: '#DC2626', fontSize: '13px', marginTop: '6px' }}>
                      {consentError}
                    </div>
                  )}
                </div>

                <Button type="submit" fullWidth isLoading={isLoading} style={{ marginTop: '16px' }}>
                  Оформить заказ
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};